import React from "react";
import { Check } from "lucide-react";
import { formatDeShort } from "@/lib/date";

export const WIZARD_STEPS = ["Person & Kontakt", "Maßnahme & Zeitraum", "Module, UE & Coaches", "Prüfen & Speichern"];

export function validateWizardStep(step, form, isLegacyOb) {
  if (step === 0) {
    if (!form.name.trim()) return "Bitte Name angeben";
    if (!form.email.trim()) return "Bitte E-Mail angeben — der Kunde bekommt einen Einladungslink";
    if (!form.phone.trim()) return "Bitte Telefonnummer angeben";
    if (!form.street.trim() || !form.postal_code.trim() || !form.city.trim()) return "Bitte vollständige Adresse angeben (Straße, PLZ, Ort)";
    if (!form.kostentraeger.trim()) return "Bitte Kostenträger angeben";
  }
  if (step === 1) {
    if (!form.customer_number.trim()) return "Bitte Kundennummer angeben";
    if (!form.measure_number.trim()) return "Bitte Maßnahmennummer angeben";
    if (!form.acquired_by_user_id) return "Bitte angeben, wer den Kunden gewonnen hat";
    if (!form.start_date || !form.end_date || form.end_date < form.start_date) return "Ungültiger Coaching-Zeitraum";
    if (!form.gesetzl_feiertage.trim()) return 'Bitte gesetzl. Feiertage im Maßnahmezeitraum angeben (oder "Keine")';
    if (!form.ferien.trim()) return 'Bitte Ferien im Maßnahmezeitraum angeben (oder "Keine")';
  }
  if (step === 2) {
    if (!form.measure_id) return "Bitte eine AVGS-Maßnahme wählen";
    if (form.sections.length === 0) return "Bitte mindestens ein Modul auswählen";
    if (form.coach_user_ids.length === 0 && !form.sections.some((s) => !!s.coach_user_id)) return "Mindestens einen Coach zuordnen (global oder pro Abschnitt)";
    for (const s of form.sections) if (!s.ue_target) return `Modul "${s.module_name || s.name}": Bitte UE angeben`;
    const empty = (v) => v === null || v === undefined || v === "";
    if (isLegacyOb) {
      if (!(Number(form.onboarding_ue) > 0)) return "Onboarding-UE muss größer als 0 sein";
      if (!(Number(form.offboarding_ue) > 0)) return "Offboarding-UE muss größer als 0 sein";
      if (empty(form.onboarding_price)) return "Onboarding: Bitte Preis angeben";
      if (empty(form.offboarding_price)) return "Offboarding: Bitte Preis angeben";
    }
    for (const mod of [...new Set(form.sections.map((s) => s.module_name || s.name))]) {
      const rep = form.sections.find((s) => (s.module_name || s.name) === mod);
      if (empty(rep.price)) return `Modul "${mod}": Bitte Preis angeben`;
      if (!rep.vz_tz) return `Modul "${mod}": Bitte VZ/TZ angeben`;
      if (!rep.duration || !rep.duration.trim()) return `Modul "${mod}": Bitte Dauer angeben`;
    }
  }
  return null;
}

export function WizardStepper({ step, onGo }) {
  return (
    <div className="px-8 pt-4 flex gap-1" data-testid="client-wizard-stepper">
      {WIZARD_STEPS.map((l, i) => (
        <button key={l} type="button" disabled={i > step} onClick={() => onGo(i)} data-testid={`client-wizard-step-${i}`}
          className={`flex-1 text-left border-t-2 pt-2 transition-colors ${i <= step ? "border-slate-900" : "border-slate-200"} disabled:cursor-default`}>
          <div className="text-[10px] font-mono text-slate-500 flex items-center gap-1">{i < step ? <Check size={10} /> : null}Schritt {i + 1}</div>
          <div className={`text-xs font-medium ${i === step ? "text-slate-900" : "text-slate-500"}`}>{l}</div>
        </button>
      ))}
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 py-2 border-b border-slate-100 text-sm" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 pt-0.5">{label}</div>
      <div className="text-slate-900">{value || "—"}</div>
    </div>
  );
}

export function WizardReview({ form, measures, coaches, onEdit }) {
  const measure = measures.find((m) => m.measure_id === form.measure_id);
  const name = (id) => coaches.find((c) => c.user_id === id)?.name || id;
  const coachIds = [...new Set([...form.coach_user_ids, ...form.sections.map((s) => s.coach_user_id).filter(Boolean)])];
  const edit = (i) => onEdit && <button type="button" onClick={() => onEdit(i)} className="text-[11px] underline text-slate-500 hover:text-slate-900" data-testid={`client-review-edit-${i}`}>ändern</button>;
  return (
    <div data-testid="client-wizard-review">
      <div className="flex justify-between items-baseline mt-1"><div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Person & Kontakt</div>{edit(0)}</div>
      <Row label="Name" value={form.name} testId="review-name" />
      <Row label="E-Mail / Telefon" value={`${form.email} · ${form.phone}`} />
      <Row label="Adresse" value={`${form.street}, ${form.postal_code} ${form.city}`} />
      <Row label="Kostenträger" value={form.kostentraeger} />
      <div className="flex justify-between items-baseline mt-5"><div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Maßnahme & Zeitraum</div>{edit(1)}</div>
      <Row label="Kd-Nr / Maßn.-Nr" value={`${form.customer_number} · ${form.measure_number}`} />
      <Row label="Zeitraum" value={`${formatDeShort(form.start_date)} – ${formatDeShort(form.end_date)}`} testId="review-period" />
      <Row label="Gewonnen von" value={name(form.acquired_by_user_id)} />
      <div className="flex justify-between items-baseline mt-5"><div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Module, UE & Coaches</div>{edit(2)}</div>
      <Row label="Maßnahme" value={measure?.name} />
      <Row label="Module" value={`${form.sections.length} ${form.sections.length === 1 ? "Abschnitt" : "Abschnitte"} · ${form.total_ue} UE gesamt`} testId="review-ue" />
      <Row label="Coaches" value={coachIds.map(name).join(", ")} testId="review-coaches" />
    </div>
  );
}
